import * as React from 'react';
import Lazy from 'lazy.js';
import { Component } from 'react';
import './SchoolEventCopyManager.scss';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { SchoolEventCopyForm } from '../SchoolEventsCopyForm/SchoolEventCopyForm';
import { SchoolForm } from 'Src/models/form';
import { getEventParticipants, isHousesEvent, isInterSchoolsEvent, isNonTeamSportEvent } from 'Src/helpers/event/event';
import { activateEvent, createEvent } from 'Src/helpers/service/admin/event';
import { addSchoolEventTeam } from 'Src/helpers/service/admin/tournamentTeams';
import { getVenueTypeByPostcode } from 'Src/helpers/venue/venue';
import { getFromHistory } from 'Src/helpers/history/history';
import { getSchoolEvent } from 'Src/helpers/service/admin/event';
import {
  getSchoolEventDetails,
  getSchoolEventTeams,
  updateSchoolEventDetails,
  deleteEvent
} from 'Src/helpers/service/admin/schoolEvents';
getVenueTypeByPostcode;
import { getSchoolsPublic } from 'Src/helpers/service/public/public';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';
import { SchoolEvent } from 'Src/models/event';
import { getHouses } from 'Src/helpers/service/admin/houses';
import { SchoolHouse } from 'Src/models/house';
import { updateSchoolEventIndividualsBatch } from 'Src/helpers/service/admin/schoolEventIndividuals';
import { EventDetail } from 'Src/models/eventDetail';
import { getEventGeneratedName } from 'Src/helpers/accessor/accessor';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import propz from 'propz';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  currentEvent: SchoolEvent;
  forms: SchoolForm[];
  isLoading: boolean;
  teams: any[];
  eventDetails: EventDetail;
  participants: any[];
  houses: SchoolHouse[];
  isErrorModalOpen: boolean;
  errorMessage: string;
}

export class SchoolEventCopyManager extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      currentEvent: undefined,
      forms: [],
      isLoading: false,
      teams: [],
      eventDetails: undefined,
      participants: [],
      houses: [],
      isErrorModalOpen: false,
      errorMessage: ''
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems();
  }

  componentWillUnmount() {
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  async setItems() {
    const { user } = this.props;
    const eventId = this.getEventId();
    try {
      const [event, forms, teams, eventDetails] = await Promise.all([
        getSchoolEvent(user, eventId),
        getAllForms(user),
        getSchoolEventTeams(user, eventId),
        getSchoolEventDetails(user, eventId)
      ]);
      const eventName = getEventGeneratedName(event, { user }) || 'Event';
      window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb', { detail: `${eventName} / Duplicate` }));
      const allSchoolIds = event.invitedSchoolIds;
      let filter: any = {
        where: { id: { $in: allSchoolIds } },
        view: {
          type: 'id_name_pic'
        },
        skip: DEFAULT_SKIP,
        limit: DEFAULT_LIMIT
      };
      const schools = await getSchoolsPublic(filter);
      const participants = getEventParticipants(event, user, schools);
      let houses: SchoolHouse[] = [];

      if (isHousesEvent(event)) {
        filter = {
          where: { id: { $in: event.houses } },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        };
        houses = await getHouses(user, filter);
      }

      this.setState({
        participants,
        houses,
        forms,
        eventDetails,
        teams,
        currentEvent: event,
        isLoading: false
      });
    } catch (error) {
      this.showRequestError(error, 'The event could not be prepared for duplication. Please try again.');
    }
  }

  showRequestError = (error: any, fallbackMessage: string) => {
    const errorMessage =
      propz.get(error, ['response', 'data', 'details', 'text'], '') ||
      propz.get(error, ['response', 'data', 'message'], '') ||
      propz.get(error, ['message'], '') ||
      fallbackMessage;
    this.setState({ isLoading: false, isErrorModalOpen: true, errorMessage });
  };

  closeErrorModal = () => this.setState({ isErrorModalOpen: false, errorMessage: '' });

  getEventId(): string {
    const { history } = this.props;
    return getFromHistory(history, 'event');
  }

  onCancelClick = () => {
    const { history } = this.props;
    const { currentEvent } = this.state;
    history.push({
      pathname: '/events/event',
      search: `id=${currentEvent.id}`
    });
  };

  onCopyEvent = data => {
    const { participants, eventDetails, currentEvent, forms } = this.state;
    const { user, history } = this.props;
    const originalAges =
      Array.isArray(currentEvent.ages) && currentEvent.ages.length > 0
        ? currentEvent.ages
        : Array.from(new Set(forms.map(form => Number(form.age))));
    const isChangeAge =
      [...data.ages]
        .map(Number)
        .sort((a, b) => a - b)
        .toString() !==
      [...originalAges]
        .map(Number)
        .sort((a, b) => a - b)
        .toString();
    const isChangeGender = data.gender !== currentEvent.gender;

    this.setState({
      isLoading: true
    });

    let eventId;
    let isEventActivated = false;
    const body = this.onModifiedDataEvent(data, user);
    const { shortDescription, ...rest } = eventDetails;
    const detailsBody = {
      ...rest,
      shortDescription: data.shortDescription
    };

    const teams = participants.map(participant => {
      return isNonTeamSportEvent(data) ? participant.individualPlayers : participant.team;
    });
    const teamsFiltered = teams
      .filter(team => typeof team !== 'undefined' && team.schoolId === user.activeSchoolId)
      .map(team => {
        const { players, ...rest } = team;

        return {
          ...rest,
          players:
            isChangeAge || isChangeGender
              ? []
              : players.map(player => ({
                  userId: player.userId,
                  permissionId: player.permissionId
                }))
        };
      });

    if (isNonTeamSportEvent(data)) {
      createEvent(user, body)
        .then(event => {
          eventId = event.id;
          const players = Lazy(
            participants
              .filter(participant => participant.school && participant.school.id === user.activeSchoolId)
              .map(participant => participant.individualPlayers || [])
          )
            .flatten()
            .toArray();
          return updateSchoolEventIndividualsBatch(user, eventId, {
            add: players.map(item => ({ userId: item.userId, permissionId: item.permissionId }))
          });
        })
        .then(() => updateSchoolEventDetails(user, eventId, detailsBody))
        .then(() => activateEvent(user, eventId))
        .then(event => {
          isEventActivated = true;
          history.push({
            pathname: '/events/event',
            search: `id=${eventId}`
          });

          return event;
        })
        .catch(async err => {
          console.error('[SchoolEventCopyManager] event copy failed', err);
          if (eventId && !isEventActivated) {
            await deleteEvent(user, eventId).catch(cleanupError => {
              console.error('[SchoolEventCopyManager] failed to remove incomplete draft copy', cleanupError);
            });
          }
          this.showRequestError(err, 'The event could not be duplicated. Please try again.');
        });
    } else {
      createEvent(user, body)
        .then(event => {
          eventId = event.id;
          return Promise.all(
            teamsFiltered.map(team => {
              return addSchoolEventTeam(user, eventId, team);
            })
          );
        })
        .then(() => updateSchoolEventDetails(user, eventId, detailsBody))
        .then(() => activateEvent(user, eventId))
        .then(() => {
          isEventActivated = true;
          history.push({
            pathname: '/events/event',
            search: `id=${eventId}`
          });
          return true;
        })
        .catch(async err => {
          console.error('[SchoolEventCopyManager] event copy failed', err);
          if (eventId && !isEventActivated) {
            await deleteEvent(user, eventId).catch(cleanupError => {
              console.error('[SchoolEventCopyManager] failed to remove incomplete draft copy', cleanupError);
            });
          }
          this.showRequestError(err, 'The event could not be duplicated. Please try again.');
        });
    }
  };

  //copy-paste
  onModifiedDataEvent(currentEvent, user: AppUser) {
    const {
      gender,
      ages,
      eventType,
      sportId,
      startTime,
      endTime,
      venue,
      participants,
      houses,
      multiparty,
      shortDescription,
      replyToEmail
    } = currentEvent;
    const { activeSchool } = user;

    //Important! Modify seconds and milliseconds in start time for navigation
    const now = new Date();
    const nowS = now.getSeconds();
    const nowMs = now.getMilliseconds();
    startTime.setSeconds(nowS, nowMs);

    const body = {
      gender: gender,
      shortDescription: shortDescription,
      multiparty: multiparty,
      eventType: eventType,
      sportId: sportId,
      venue: venue,
      ages: ages.map(age => Number(age)),
      houses: houses.map(house => house.id),
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      invitedSchoolIds: isInterSchoolsEvent(currentEvent)
        ? participants.map(participant => participant.id)
        : [user.activeSchoolId],
      replyToEmail: replyToEmail
    };

    switch (true) {
      case typeof venue !== 'undefined' && venue.venueType === 'TBD':
        delete body.venue.postcodeId;
        body.venue.venueType = 'TBD';
        break;

      case typeof venue !== 'undefined' && typeof venue.point !== 'undefined' && venue.point.coordinates.length > 0:
        if (venue.id === 'TBD') {
          delete body.venue.postcodeId;
          body.venue.venueType = 'TBD';
          break;
        }
        if (typeof venue.postcodeId === 'undefined') {
          body.venue.postcodeId = venue.id;
        }
        body.venue.venueType = getVenueTypeByPostcode(venue, activeSchool);
        break;

      case typeof venue !== 'undefined' && typeof venue.point === 'undefined':
        if (venue.id === 'TBD') {
          delete body.venue.postcodeId;
          body.venue.venueType = 'TBD';
          break;
        }
        body.venue.venueType = getVenueTypeByPostcode(venue, activeSchool);
        break;

      default:
        delete body.venue;
    }
    if (typeof body.venue !== 'undefined') {
      delete body.venue.id;
      delete body.venue.isHome;
      delete body.venue.name;
      delete body.venue.postcode;
    }

    return body;
  }

  //return to initial state
  goBack = () => {
    this.setState({
      teams: []
    });
  };

  renderSchoolEventForm(): React.ReactNode {
    const { forms, currentEvent, participants, houses, eventDetails } = this.state;
    const { user } = this.props;
    const isExistSchoolEvent = typeof currentEvent !== 'undefined';
    if (isExistSchoolEvent) {
      return (
        <SchoolEventCopyForm
          schoolEvent={currentEvent}
          eventDetails={eventDetails}
          participants={participants}
          houses={houses}
          onSubmit={this.onCopyEvent}
          onCancel={this.onCancelClick}
          user={user}
          forms={forms}
        />
      );
    } else {
      return '';
    }
  }

  render() {
    const { isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="bSchoolEventManager eSchoolEventCopyManager">
        <SimpleModal
          isOpen={this.state.isErrorModalOpen}
          title="Something went wrong"
          onCloseClick={this.closeErrorModal}
          onBackdropClick={this.closeErrorModal}
          showFooter={false}
          isBodyBold={false}
        >
          <p>{this.state.errorMessage}</p>
        </SimpleModal>
        <header className="eSchoolEventCopyManager__hero">
          <span className="eSchoolEventCopyManager__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <rect x="8" y="8" width="12" height="12" rx="2" />
              <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
            </svg>
          </span>
          <div>
            <div className="eSchoolEventCopyManager__eyebrow">Events</div>
            <h1>Duplicate event</h1>
            <p>Create a new event using the existing event details and participants as a starting point.</p>
          </div>
        </header>
        {this.renderSchoolEventForm()}
      </div>
    );
  }
}
